import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isProblemError } from '../api/client';
import { getGetMeQueryKey } from '../api/generated/identity/identity';
import type { Me } from '../api/generated/model';

function isUnauthorized(error: unknown): boolean {
  return isProblemError(error) && error.status === 401;
}

/**
 * Cliente de dados da SPA.
 *
 * Duas decisoes que valem para tudo:
 *
 * - **Nao repetir erro 4xx.** Repetir um 401 ou um 422 nao muda a resposta e so
 *   atrasa o retorno para a pessoa. Repetir um 429 piora o que causou o 429.
 * - **401 em qualquer chamada invalida a sessao em cache.** A proxima
 *   renderizacao encontra a sessao ausente e o roteador leva ao login. Nenhum
 *   token e tocado em JavaScript: o cookie e `HttpOnly` e quem o expira e o
 *   servidor.
 * - **Cache nao atravessa pessoa.** Ver `forgetPreviousPerson` abaixo.
 */
export function createQueryClient(): QueryClient {
  // Os caches sao construidos antes do cliente e precisam apontar de volta para
  // ele. A caixa e fixa; o que muda e o conteudo, ja preenchido quando o
  // primeiro erro chega.
  const ref: { current: QueryClient | undefined } = { current: undefined };

  const invalidateSession = (error: unknown): void => {
    if (!isUnauthorized(error)) return;
    void ref.current?.invalidateQueries({ queryKey: getGetMeQueryKey() });
  };

  const sessionQueryHash = JSON.stringify(getGetMeQueryKey());

  const client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Invalidar a propria consulta de sessao quando **ela** falha com 401
        // produziria uma repeticao infinita: refetch, 401, invalida, refetch.
        if (JSON.stringify(query.queryKey) === sessionQueryHash) return;
        invalidateSession(error);
      },
    }),
    mutationCache: new MutationCache({ onError: invalidateSession }),
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: { retry: false },
    },
  });

  ref.current = client;
  forgetPreviousPerson(client, sessionQueryHash);

  return client;
}

/**
 * O cache pertence a UMA pessoa, e vai embora quando ela vai.
 *
 * `useLogoutAction` ja limpa tudo no botao Sair, e por isso o defeito nao
 * aparecia em teste nenhum: ele mora na saida que NAO passa por botao — cookie
 * expirado, sessao revogada, API reiniciada. Ali o 401 so invalida a consulta de
 * sessao; agenda, mensagens, faturas e folha da pessoa anterior continuam
 * inteiras no cache, porque o `QueryClient` e unico e nasce em `App.tsx`, fora
 * do roteador. Quem entrasse em seguida na mesma aba — recepcao, laboratorio,
 * casa compartilhada — abriria cada tela com o dado do anterior renderizado de
 * imediato, sem nenhuma requisicao, enquanto o refetch corria por baixo.
 *
 * A limpeza e disparada pela TROCA DE PESSOA, e nao pelo 401. Sao dois motivos:
 * o 401 chega antes de haver com quem trocar — apagar ali derruba o cache de uma
 * sessao que ainda pode voltar viva —, e limpar em cima de consultas montadas
 * faz cada uma refazer a chamada que acabou de receber 401. Na troca de pessoa
 * nada da pessoa anterior esta montado: o roteador ja levou ao login.
 *
 * A consulta de sessao e a unica preservada, porque e ela quem acabou de dizer
 * quem entrou. Removida junto, o proprio evento apagaria a resposta que o
 * disparou e a SPA voltaria ao login em ciclo.
 *
 * Recusada a alternativa de trocar o `QueryClient` inteiro por pessoa: o cliente
 * e criado uma vez e passado ao provedor, e recria-lo remonta a arvore inteira —
 * troca uma limpeza de cache por um recarregamento disfarcado.
 */
function forgetPreviousPerson(client: QueryClient, sessionQueryHash: string): void {
  let personId: string | undefined;

  client.getQueryCache().subscribe((event) => {
    if (JSON.stringify(event.query.queryKey) !== sessionQueryHash) return;

    // O 401 nao apaga `data`: o `Me` anterior segue no cache ate alguem gravar
    // outro. E exatamente por isso que a comparacao aqui e por `id`, e nao por
    // "tem sessao?" — a troca so e visivel quando a sessao nova chega.
    const next = (event.query.state.data as Me | undefined)?.id;
    if (next === personId) return;

    const previous = personId;
    personId = next;

    // Primeira sessao da aba: nao ha pessoa anterior, e nao ha o que esquecer.
    if (previous === undefined) return;

    client.removeQueries({
      predicate: (query) => JSON.stringify(query.queryKey) !== sessionQueryHash,
    });
  });
}
